import { useRef, useState, type RefObject } from "react";
import { useIntl } from "react-intl";
import { sendAnalyticsInteractionEvent } from "src/analytics/sendAnalyticsEvent";
import type { AutocompletePlace } from "src/api/v1.6/autocomplete";
import { AddDestinationInput } from "src/components/TripPlanner/AddDestination/AddDestinationInput";
import type { GeocodedPlace } from "src/GeocodedPlace";
import Plus from "src/design-system/icons/Plus.svg?react";
import { Button } from "react-aria-components";
import { tw } from "src/design-system/tw";
import { useTripPlannerContext } from "../hooks/useTripPlannerContext";
import { messages } from "./AddDestinationInline.messages";

type AddDestionationInlineType = {
  addDestinationRef: RefObject<HTMLDivElement | null>;
  onSelectDestination(place: AutocompletePlace): void;
};

export default function AddDestinationInline(props: AddDestionationInlineType) {
  const intl = useIntl();
  const { tripPlannerDetails } = useTripPlannerContext();
  const addDestinationRef = useRef<HTMLInputElement>(null);
  const [isQueryEmpty, setIsQueryEmpty] = useState(true);
  const [isFocused, setIsFocused] = useState(false);
  const lastPlace: GeocodedPlace | undefined =
    tripPlannerDetails.places[tripPlannerDetails.places.length - 1];

  function handleOnChange(query: string) {
    setIsQueryEmpty(query === "");
  }

  function handleAutocompleteSelectOption(place: AutocompletePlace) {
    props.onSelectDestination(place);
    setIsQueryEmpty(true);
    sendAnalyticsInteractionEvent({
      category: "TripPlanner",
      action: "Select:AddDestinationInline",
    });
  }

  function handleClear() {
    setIsQueryEmpty(true);
  }

  function handleFocus() {
    sendAnalyticsInteractionEvent({
      category: "TripPlanner",
      action: "Click:AddDestinationInline",
    });
    addDestinationRef.current?.focus();
  }

  return (
    <div className="top-0.5 mt-3 flex pl-12" ref={props.addDestinationRef}>
      <Button
        className="absolute top-2 left-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-fill-brand hover:cursor-pointer md:hover:bg-fill-brand-hover"
        onClick={handleFocus}
      >
        <Plus className="text-icon-on-bg-fill" />
      </Button>
      <AddDestinationInput
        onSelectOption={handleAutocompleteSelectOption}
        isResetOnSelect
        AutocompleteListWrapper={AutocompleteListWrapper}
        hideList={isQueryEmpty}
        onChange={handleOnChange}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        isFocused={isFocused}
        showClearIcon={!isQueryEmpty}
        handleClear={handleClear}
        id="add-destination-inline"
        type={0}
        placeholder={intl.formatMessage(
          isFocused ? messages.enterCity : messages.addDestination,
        )}
        addDestinationRef={addDestinationRef}
        transparentBackground={false}
        noShadow
        fullWidth
        curvedBorder
        solidBackground
        filteredPlaces={lastPlace ? [lastPlace] : undefined}
      />
    </div>
  );
}

type AutocompleteListWrapperProps = React.PropsWithChildren<{
  style?: React.CSSProperties;
  className?: string;
}>;

export function AutocompleteListWrapper({
  ref,
  children,
  style,
  className,
}: AutocompleteListWrapperProps & {
  ref: React.RefObject<HTMLDivElement | null>;
}) {
  return (
    <div
      ref={ref}
      style={style}
      className={tw(
        "absolute top-full left-0 z-modal max-h-100 w-full overflow-y-auto rounded-md bg-surface shadow-md",
        className,
      )}
    >
      {children}
    </div>
  );
}
